import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Building2, Check, ExternalLink, Plus, RefreshCw, User } from 'lucide-react';
import AssistantCompanyProfileResource, {
	PagePersonExtract,
	PageCompanyContact,
	PagePersonCompanyOption
} from 'Resources/AssistantCompanyProfile';
import { openCrmEntity } from 'App/shadcn/pages/Assistant/openCrmEntity';
import type { PageContext } from 'App/shadcn/pages/AIWorkspace/Chat/buildPageContextString';
import { parseLinkedInName } from './pageCompanyDomain';
import FindContactDetails, { type FoundContactDetails } from './FindContactDetails';
import useCrmContactInfo from './useCrmContactInfo';
import ContactCreateActions from './ContactCreateActions';
import useAirscaleLookup from './useAirscaleLookup';
import { accountManagerMeta } from './accountManagerMeta';
import InUpsalesBanner from './InUpsalesBanner';
import T from 'Components/Helpers/translate';

type Props = {
	pageContext: PageContext;
	// Per-URL extraction cache owned by EmbedAIChat so it survives tab switches.
	extractCache: Map<string, PagePersonExtract>;
	/**
	 * Whether the person on `url` is a contact, already or just added, so the tab shows a check
	 * rather than an add (WEB-15180). Keyed by URL: this runs before the parent sees the new page.
	 */
	onInUpsalesChange: (url: string, inUpsales: boolean) => void;
};
type Phase = 'loading' | 'error' | 'ready';

// Send only the top of the page to the extractor: a LinkedIn profile's identity
// (name, headline, current role) sits near the start, so a smaller slice is enough
// and keeps the request — and the model's token load — light.
const EXTRACT_TEXT_LIMIT = 4000;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
	<div className="embed-add-fv">
		<span className="embed-add-fv-label">{label}</span>
		<span className="embed-add-fv-value">{children}</span>
	</div>
);

const ExternalLinkValue: React.FC<{ href: string; text: string }> = ({ href, text }) => (
	<a className="embed-add-fv-link" href={href} target="_blank" rel="noopener noreferrer">
		{text}
		<ExternalLink />
	</a>
);

const AddPersonTab: React.FC<Props> = ({ pageContext, extractCache, onInUpsalesChange }) => {
	const [phase, setPhase] = useState<Phase>('loading');
	const [extract, setExtract] = useState<PagePersonExtract | null>(null);
	// An existing contact is not re-added unless the rep asks to add them anyway.
	const [addAnyway, setAddAnyway] = useState(false);
	// The company search text — seeded from the extracted employer, editable.
	const [companyQuery, setCompanyQuery] = useState('');
	const [companyOptions, setCompanyOptions] = useState<PagePersonCompanyOption[]>([]);
	// A CRM client id, 'new' = create a company from the query text, null = not chosen yet.
	// Create is preselected only when the search finds no company the rep could mean (WEB-15178).
	const [selectedCompany, setSelectedCompany] = useState<number | 'new' | null>(null);
	const [searching, setSearching] = useState(false);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(false);
	const [added, setAdded] = useState(false);
	const [addedClient, setAddedClient] = useState<{ id: number; name: string } | null>(null);
	// The contact the add just created (or matched), so its card offers a call and an activity (WEB-15181).
	const [addedContactId, setAddedContactId] = useState<number | null>(null);
	// Email and phone number found by Lookup, added with the contact (WEB-13374).
	const [found, setFound] = useState<FoundContactDetails>({});
	const canLookUp = useAirscaleLookup();
	// The email and numbers the CRM already has for this person, if they are a contact (WEB-13771).
	const {
		info: crm,
		loading: crmLoading,
		contact: crmContact,
		reload: reloadCrm
	} = useCrmContactInfo(extract?.person?.existsInCrm ? extract.person.contactId : addedContactId ?? undefined);
	const inUpsales = Boolean(extract?.person?.existsInCrm) || added;
	useEffect(() => {
		onInUpsalesChange(pageContext.url, inUpsales);
	}, [pageContext.url, inUpsales, onInUpsalesChange]);
	// Always the current URL, so a late response for a previous page is ignored.
	const latestUrl = useRef(pageContext.url);
	latestUrl.current = pageContext.url;
	// The page the server extract last answered for. Until it has, only the name from the title is
	// known, not whether this is a contact, so the buttons wait rather than move under the cursor when
	// the answer lands (WEB-15179). By URL alone: a title change on the same profile (LinkedIn's
	// notification count) must not take away a lookup already run.
	const [answeredFor, setAnsweredFor] = useState('');
	// True once the user starts filling in the add form for this page, so a late server
	// enrichment (or a re-capture of the same page) can't reset what they're editing.
	const engagedRef = useRef(false);
	// The profile whose extract is on screen, by URL and the name in its title.
	const shownRef = useRef({ url: '', name: '' });
	useEffect(() => {
		engagedRef.current = false;
	}, [pageContext.url]);

	const applyExtract = useCallback((result: PagePersonExtract) => {
		setExtract(result);
		setCompanyQuery(result.company?.name ?? result.person?.companyName ?? '');
		setSelectedCompany(result.company?.crm.exists ? result.company.crm.clientId ?? null : null);
		setAddAnyway(false);
		setAdded(false);
		setAddedClient(null);
		setAddedContactId(null);
		setPhase('ready');
	}, []);

	// The name shown instantly, parsed from the page title — the server extract enriches it.
	const buildLocalExtract = useCallback((): PagePersonExtract | null => {
		const name = parseLinkedInName(pageContext.title);
		if (!name) return null;
		return { person: { name, linkedin: pageContext.url }, company: null };
	}, [pageContext.title, pageContext.url]);

	const runExtract = useCallback(() => {
		// Don't disturb the form once the user is filling it in for this page.
		if (engagedRef.current) {
			setAnsweredFor(pageContext.url);
			return;
		}
		// By URL and title: an extract read from a capture whose title still named the previous
		// profile must not stick to the new URL once a fresh capture arrives (WEB-13771).
		const cacheKey = `${pageContext.url}\n${pageContext.title}`;
		const seed = buildLocalExtract();
		const shown = { url: pageContext.url, name: seed?.person?.name ?? '' };
		const cached = extractCache.get(cacheKey);
		if (cached) {
			applyExtract(cached);
			shownRef.current = shown;
			setAnsweredFor(pageContext.url);
			return;
		}
		// Show the title-derived name immediately so a profile never sits on a blank
		// "reading…" or a false "no person found" while the server extract is in flight.
		// Not over the same person: a title change on their profile (LinkedIn's notification count)
		// keeps what is shown, rather than drop whether they are a contact until the server answers
		// again (WEB-15179). Another name on the same URL is a new person, so the buttons wait again.
		const sameProfile = Boolean(seed) && shownRef.current.url === shown.url && shownRef.current.name === shown.name;
		if (seed && !sameProfile) {
			applyExtract(seed);
			shownRef.current = shown;
			setAnsweredFor('');
		} else if (!seed) {
			shownRef.current = shown;
			setPhase('loading');
			// Not the previous profile's person: the tab would report them as this page's.
			setExtract(null);
			setCompanyQuery('');
		}
		const reqUrl = pageContext.url;
		// An answer for a person no longer on screen is dropped: after a navigation the first capture
		// can still carry the previous profile's title, and its answer may land after the next one's seed.
		const stale = () => reqUrl !== latestUrl.current || shownRef.current.name !== shown.name;
		AssistantCompanyProfileResource.pagePersonExtract({
			url: pageContext.url,
			title: pageContext.title,
			text: pageContext.text.slice(0, EXTRACT_TEXT_LIMIT)
		})
			.then(result => {
				if (stale()) return;
				setAnsweredFor(reqUrl);
				if (result.person) {
					// Cache only a positive result — a null means the page hadn't rendered yet and
					// must be retried when a fuller capture arrives (else it sticks until reload).
					extractCache.set(cacheKey, result);
					// An existing contact still wins over a form the rep opened before the check came back,
					// or "Next" on the instant name would add them again (WEB-15180).
					if (!engagedRef.current || result.person.existsInCrm) {
						applyExtract(result);
						shownRef.current = shown;
					}
				} else if (!seed) {
					setExtract(null);
					setPhase('ready');
				}
			})
			.catch(() => {
				if (stale()) return;
				setAnsweredFor(reqUrl);
				if (!seed) setPhase('error');
			});
	}, [pageContext.url, pageContext.title, pageContext.text, extractCache, applyExtract, buildLocalExtract]);

	useEffect(() => {
		setSaveError(false);
		runExtract();
	}, [runExtract]);

	const existingContactId = extract?.person?.existsInCrm ? extract.person.contactId : undefined;
	// Whether this is a contact, and what the CRM already has, decide which buttons show: until both
	// are known a placeholder holds their place (WEB-15179).
	const settling = answeredFor !== pageContext.url || crmLoading;
	// The company is picked on the same page as the person, so adding takes one click (WEB-15177).
	const pickingCompany = !settling && (!existingContactId || addAnyway);

	// Search the CRM for companies matching the query while the company picker is shown.
	useEffect(() => {
		const q = companyQuery.trim();
		if (!pickingCompany || !q) {
			setCompanyOptions([]);
			setSearching(false);
			return;
		}
		setSearching(true);
		// A response for an earlier query (or the previous profile) must not replace this one's.
		let current = true;
		const timer = setTimeout(() => {
			AssistantCompanyProfileResource.pagePersonCompanies(q)
				.then(list => {
					if (!current) return;
					setCompanyOptions(list);
					setSearching(false);
					if (!list.length) setSelectedCompany(prev => prev ?? 'new');
				})
				.catch(() => {
					if (!current) return;
					setCompanyOptions([]);
					setSearching(false);
				});
		}, 350);
		return () => {
			current = false;
			clearTimeout(timer);
		};
	}, [pickingCompany, companyQuery]);

	const submit = () => {
		if (!extract?.person || saving || added) return;
		const person = extract.person;
		const company = extract.company;
		const trimmed = companyQuery.trim();
		const createNew = selectedCompany === 'new';
		if (selectedCompany === null || (createNew && !trimmed)) return;
		setSaving(true);
		setSaveError(false);
		// Creating a new company by name — reuse the extracted prospecting/domain only
		// when the name is still the one we resolved (the user didn't change it).
		const reuseExtract = createNew && Boolean(company) && trimmed.toLowerCase() === company!.name.toLowerCase();
		const contact: PageCompanyContact = {
			name: person.name,
			role: person.title,
			company: trimmed || undefined,
			linkedin: person.linkedin,
			email: found.email,
			phone: found.phone
		};
		AssistantCompanyProfileResource.pageCompanyAdd({
			clientId: createNew ? undefined : selectedCompany,
			prospectingId: reuseExtract ? company?.prospectingId : undefined,
			name: createNew ? trimmed : undefined,
			domain: reuseExtract ? company?.domain : undefined,
			orgNumber: reuseExtract ? company?.orgNumber ?? undefined : undefined,
			contacts: [contact]
		})
			.then(result => {
				setSaving(false);
				setAdded(true);
				setAddedClient({ id: result.clientId, name: result.clientName });
				setAddedContactId(result.contactIds[0] ?? null);
				// The cached extract still says "not a contact": drop it so coming back to this
				// profile checks again and shows them as in Upsales (WEB-15180). Every title of
				// the URL: it was cached under the title it had then, which a "(3) " prefix changes.
				for (const key of [...extractCache.keys()]) {
					if (key.startsWith(`${pageContext.url}\n`)) extractCache.delete(key);
				}
			})
			.catch(() => {
				setSaving(false);
				setSaveError(true);
			});
	};

	const startAddAnyway = () => {
		engagedRef.current = true;
		setAddAnyway(true);
	};

	const selectCompany = (company: number | 'new') => {
		engagedRef.current = true;
		setSelectedCompany(company);
	};

	if (phase === 'loading') {
		return <div className="embed-add embed-add-status">{T('assistant.embedPersonReading')}</div>;
	}
	if (phase === 'error') {
		return (
			<div className="embed-add embed-add-status">
				<div>{T('assistant.embedPersonError')}</div>
				<button type="button" className="embed-add-btn" onClick={runExtract}>
					<RefreshCw />
					{T('assistant.embedAddRetry')}
				</button>
			</div>
		);
	}
	if (!extract?.person) {
		return <div className="embed-add embed-add-status">{T('assistant.embedPersonNone')}</div>;
	}

	const person = extract.person;
	const pageCompany = extract.company;
	const companyName = pageCompany?.name ?? person.companyName;
	const trimmedQuery = companyQuery.trim();
	const canSubmit = !saving && selectedCompany !== null && (selectedCompany !== 'new' || Boolean(trimmedQuery));
	const linkedinText = person.linkedin ? person.linkedin.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '') : '';

	if (added) {
		return (
			<div className="embed-add embed-add-status">
				<div className="embed-add-success-check">
					<Check />
				</div>
				<div className="embed-add-success-title">{T('assistant.embedPersonAdded')}</div>
				<div className="embed-add-success-sub">
					{addedClient ? `${person.name} · ${addedClient.name}` : person.name}
				</div>
				{addedClient ? (
					<button
						type="button"
						className="embed-add-btn embed-add-btn-primary"
						onClick={() => openCrmEntity('client', addedClient.id)}
					>
						<ExternalLink />
						{T('assistant.embedAddOpen')}
					</button>
				) : null}
				{crmContact ? <ContactCreateActions contact={crmContact} /> : null}
			</div>
		);
	}

	return (
		<div className="embed-add">
			<div className="embed-add-scroll">
				{saveError ? <div className="embed-add-error">{T('assistant.embedAddSaveError')}</div> : null}

				{existingContactId ? (
					<InUpsalesBanner contactId={existingContactId} clientName={crm?.client?.name ?? null} />
				) : null}
				<div className="embed-add-item embed-add-item-static">
					<User className="embed-add-item-icon" />
					<span className="embed-add-item-info">
						<span className="embed-add-item-name">{person.name}</span>
						{person.title ? <span className="embed-add-item-meta">{person.title}</span> : null}
					</span>
				</div>
				{/* The email and phone first, then the company, like Lusha's card (WEB-15175). */}
				{settling ? (
					<div className="embed-add-settling" aria-busy="true">
						{canLookUp && person.linkedin ? <span className="embed-add-settling-find" /> : null}
						<span className="embed-add-settling-btn" />
					</div>
				) : canLookUp && person.linkedin ? (
					<FindContactDetails
						linkedinUrl={person.linkedin}
						onFound={setFound}
						existingContactId={existingContactId}
						inCrm={crm ? { email: !!crm.email, phoneNumber: !!crm.cellPhone } : undefined}
						onFilled={reloadCrm}
					/>
				) : null}
				<div className="embed-add-fields">
					{/* Not again what the card above already shows, once "Add to the contact" saved it (WEB-15192). */}
					{crm?.email && crm.email !== found.email ? (
						<Field label={T('default.email')}>{crm.email}</Field>
					) : null}
					{crm?.cellPhone && crm.cellPhone !== found.phone ? (
						<Field label={T('default.cellPhone')}>{crm.cellPhone}</Field>
					) : null}
					{crm?.phone ? <Field label={T('default.phone')}>{crm.phone}</Field> : null}
					{person.linkedin ? (
						<Field label={T('default.linkedin')}>
							<ExternalLinkValue href={person.linkedin} text={linkedinText} />
						</Field>
					) : null}
				</div>
				{companyName ? (
					<div className="embed-add-company">
						<div className="embed-add-item embed-add-item-static">
							<Building2 className="embed-add-item-icon" />
							<span className="embed-add-item-info">
								<span className="embed-add-item-name">{companyName}</span>
							</span>
						</div>
						{/* About, size and industry, like Lusha's card (WEB-15186). */}
						{pageCompany?.about ? (
							<p className="embed-add-company-about" title={pageCompany.about}>
								{pageCompany.about}
							</p>
						) : null}
						{pageCompany?.domain ||
						pageCompany?.orgNumber ||
						pageCompany?.employees ||
						pageCompany?.industry ? (
							<div className="embed-add-fields">
								{pageCompany.domain ? (
									<Field label={T('default.webpage')}>
										<ExternalLinkValue
											href={`https://${pageCompany.domain}`}
											text={pageCompany.domain}
										/>
									</Field>
								) : null}
								{pageCompany.orgNumber ? (
									<Field label={T('default.orgNumber')}>{pageCompany.orgNumber}</Field>
								) : null}
								{/* 0 is what a holding company files, not a size worth showing. */}
								{pageCompany.employees ? (
									<Field label={T('default.employees')}>{pageCompany.employees}</Field>
								) : null}
								{pageCompany.industry ? (
									<Field label={T('industry')}>
										<span title={pageCompany.industry}>{pageCompany.industry}</span>
									</Field>
								) : null}
							</div>
						) : null}
					</div>
				) : null}
				{settling ? null : existingContactId ? (
					<>
						{crmContact ? <ContactCreateActions contact={crmContact} /> : null}
						{/* A contact whose saved LinkedIn is this profile is this person; a name-only
						match may be a namesake, who can still be added. */}
						{person.sameProfile || addAnyway ? null : (
							<button type="button" className="embed-add-back" onClick={startAddAnyway}>
								{T('assistant.embedPersonAddAnyway')}
							</button>
						)}
					</>
				) : null}
				{pickingCompany ? (
					<>
						<label className="embed-add-field">
							<span>{T('default.client')}</span>
							<input
								value={companyQuery}
								onChange={e => {
									engagedRef.current = true;
									setCompanyQuery(e.target.value);
									setSelectedCompany(null);
								}}
								placeholder={T('default.client')}
							/>
						</label>

						<div className="embed-add-company-list">
							{companyOptions.map(opt => {
								const meta = [opt.orgNumber, opt.domain, accountManagerMeta(opt.accountManagers)]
									.filter(Boolean)
									.join(' · ');
								const isSelected = selectedCompany === opt.clientId;
								return (
									<button
										key={opt.clientId}
										type="button"
										className={`embed-add-item${isSelected ? ' is-selected' : ''}`}
										onClick={() => selectCompany(opt.clientId)}
									>
										<Building2 className="embed-add-item-icon" />
										<span className="embed-add-item-info">
											<span className="embed-add-item-name">{opt.name}</span>
											{meta ? <span className="embed-add-item-meta">{meta}</span> : null}
										</span>
										{isSelected ? (
											<span className="embed-add-item-done">
												<Check />
											</span>
										) : null}
									</button>
								);
							})}

							{trimmedQuery ? (
								<button
									type="button"
									className={`embed-add-item${selectedCompany === 'new' ? ' is-selected' : ''}`}
									onClick={() => selectCompany('new')}
								>
									<Plus className="embed-add-item-icon" />
									<span className="embed-add-item-info">
										<span className="embed-add-item-name">{trimmedQuery}</span>
										<span className="embed-add-item-meta">
											{T('assistant.embedPersonWillCreateCompany')}
										</span>
									</span>
									{selectedCompany === 'new' ? (
										<span className="embed-add-item-done">
											<Check />
										</span>
									) : null}
								</button>
							) : null}

							{searching ? <div className="embed-add-fv-label">{T('default.searching')}</div> : null}
						</div>

						<button
							type="button"
							className="embed-add-btn embed-add-btn-primary"
							disabled={!canSubmit}
							onClick={submit}
						>
							{T('prospectingAgent.addToCRM')}
						</button>
					</>
				) : null}
			</div>
		</div>
	);
};

export default AddPersonTab;
